import { Component, DoCheck, ElementRef, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import {RolesService} from '../../core/services/roles/roles.service';
import {EndpointTestingService} from '../../core/services/endpoint-testing/endpoint-testing.service';
import {interval, Observable, of} from 'rxjs';
import {ApiConfig, DefaultPoolName} from '../../shared/models';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import {map, take, tap, timeInterval} from 'rxjs/operators';
import values from 'lodash/values';
import forEach from 'lodash/forEach';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import {CMSConditions} from '../../shared/models/cms-data';
import {CMSService} from '../../shared/components/cms/service/cms.service';
import {FlowManagerService} from '../flow/services/flow-manager.service';
import {Environment, SharedService} from '../../core/services/store/shared-store/shared-storage.service';
import {StageRequest} from '../../shared/models/flow-manager-core';
import {swalMini} from '../../shared/utils/utils';
import {sanitizeContentType} from '../../shared/utils/monacoEditorUtils/monaco-editor-config';
import { MatTabChangeEvent, MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { ActiveFormService } from '../flow/services/active-form.service';
import { MatToolbar } from '@angular/material/toolbar';
import { NgClass, NgStyle, AsyncPipe, KeyValuePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { LoadingIconComponent } from '../../shared/components/loading-icon/loading-icon.component';
import { CMSComponent } from '../../shared/components/cms/cms.component';
import { MatSelect, MatOption } from '@angular/material/select';

const URL_REGEX: RegExp =
  /^(?:http[s]?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:\/?#[\]@!\$&'\(\)\*\+\|,;=.\s\%]+$/;
const URL_REGEX_FOR_INTERNAL_USER = '^[\\w\\W]*\\[\\[[\\w\\s\\W]+\\]\\][\\w\\W]*$';

interface editFormFieldItem {
  id: string;
  type: 'input' | 'select';
  control: string;
  label: string;
  placeholder: string;
  optional: boolean;
  cssClass?: string;
  optionsList?: object[] | string[];
  tooltipId?: string;
  popover?: object;
  fieldType?: string;
}

@Component({
    selector: 'app-api-integration',
    templateUrl: './api-integration.component.html',
    styleUrls: ['./api-integration.component.scss'],
    imports: [MatToolbar, NgClass, MatIcon, LoadingIconComponent, MatTabGroup, MatTab, MatTabLabel, ReactiveFormsModule, CMSComponent, MatSelect, MatOption, NgStyle, AsyncPipe, KeyValuePipe]
})
export class ApiIntegrationComponent implements OnInit, DoCheck, OnDestroy {
  public methodOptions: string[] = ['GET', 'POST', 'PUT', 'DELETE'];
  public responseTypeOptions: object[] = [
    { value: 'application/json', title: 'application/json' },
    { value: 'application/xml', title: 'application/xml' },
    { value: 'file', title: 'application/octet-stream' },
    { value: 'flow/no-content', title: 'flow/no-content' },
    { value: 'text/plain', title: 'text/plain' },
    { value: 'text/xml', title: 'text/xml' },
    { value: 'multipart/form-data', title: 'multipart/form-data' },
  ];
  public requestTypeOptions: object[] = [
    { value: 'application/json', title: 'application/json' },
    { value: 'application/soap+xml', title: 'application/soap+xml' },
    { value: 'application/x-www-form-urlencoded', title: 'application/x-www-form-urlencoded' },
    { value: 'text/plain', title: 'text/plain' },
    { value: 'text/xml', title: 'text/xml' },
    { value: 'multipart/form-data', title: 'multipart/form-data' },
  ];
  public requestCharsetOptions: object[] = [
    { value: 'UTF-8', title: 'UTF-8' },
    { value: 'ASCII', title: 'ASCII' },
    { value: 'ISO-8859-1', title: 'ISO-8859-1' },
  ];

  public apiFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  public api: ApiConfig = new ApiConfig();
  public editFormFields: editFormFieldItem[] = [
    {
      id: 'name',
      type: 'input',
      control: 'apiName',
      label: 'Name',
      placeholder: 'Enter a name for the endpoint',
      optional: false,
      cssClass: 'name',
      optionsList: [],
      tooltipId: '',
      fieldType: 'text'
    },
    {
      id: 'URL',
      type: 'input',
      control: 'url',
      label: 'URL',
      placeholder: 'Enter a URL, e.g. http://clickatell.com/endpoint',
      optional: false,
      cssClass: '',
      optionsList: [],
      tooltipId: '',
      fieldType: 'url'
    },
    {
      id: 'baseElement',
      type: 'input',
      control: 'baseElement',
      label: 'Base Element',
      placeholder: 'Define the API\'s base element',
      optional: true,
      cssClass: '',
      optionsList: [],
      tooltipId: '',
      popover: {
        size: '17'
      },
      fieldType: 'text'
    },
    {
      id: 'elementValidation',
      type: 'input',
      control: 'elementValidation',
      label: 'Element Validation',
      placeholder: 'Add element validation',
      optional: false,
      cssClass: '',
      optionsList: [],
      tooltipId: '',
      popover: {
        size: '17'
      },
      fieldType: 'text'
    },
    {
      id: 'method',
      type: 'select',
      control: 'method',
      label: 'Method',
      placeholder: 'Select Method',
      optional: false,
      cssClass: 'method',
      optionsList: this.methodOptions,
      tooltipId: ''
    },
    {
      id: 'request',
      type: 'select',
      control: 'requestType',
      label: 'Request Content-Type',
      placeholder: 'Select request content-type',
      optional: false,
      cssClass: 'request',
      optionsList: this.requestTypeOptions,
      tooltipId: ''
    },
    {
      id: 'response',
      type: 'select',
      control: 'responseType',
      label: 'Response Content-Type',
      placeholder: 'Select response content-type',
      optional: false,
      cssClass: 'response',
      optionsList: this.responseTypeOptions,
      tooltipId: ''
    },
    {
      id: 'charset',
      type: 'select',
      control: 'charsetType',
      label: 'Request Charset',
      placeholder: 'Select request charset',
      optional: true,
      cssClass: 'charset',
      optionsList: this.requestCharsetOptions,
      tooltipId: ''
    },
  ];

  public isUserCanEdit: boolean;
  public isUserInternal: boolean;
  public isNewApiButtonDisplayed: boolean;
  globalEnvironment: Environment;
  apiList: Observable<Map<String, ApiConfig>>;
  selectedApiName: string;
  selectedApiUrlDisplay: string;
  rawResultText: string;
  rawResultMode: string;
  testBodyText: string;
  testQueryParams = [{}];
  testHeaders: [{}] = [{}];
  isNewEndpoint = false;
  isTesting = false;
  isSaving = false;
  isDeleting = false;
  isCancelling = false;
  isTimeOut = false;
  testDurationCounter: any;
  engineVariables = {};
  showResults = false;
  testSelectedIndex = 0;
  apiSelectedIndex = 0;
  saveClicked = false;
  apiListForDisplay: Observable<any>;
  sandboxApis: object;
  bannerCmsData;
  isFormDataValid: boolean = false;
  @ViewChild('bodyEditor') bodyEditor: ElementRef;
  @ViewChild('resultEditor') resultEditor: ElementRef;
  @ViewChild('saveAPIIntegration', { static: true }) saveAPIIntegration: TemplateRef<any>;
  private monacoBodyEditor: monaco.editor.IStandaloneCodeEditor;
  public activeTab: string;
  private monacoResults: monaco.editor.IStandaloneCodeEditor;

  constructor(
    private rolesService: RolesService,
    private flowManagerService: FlowManagerService,
    private endpointTestingService: EndpointTestingService,
    public dialog: MatDialog,
    public activeFormService: ActiveFormService,
    private formBuilder: UntypedFormBuilder,
    public sharedService: SharedService,
    public cmsService: CMSService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.loadApiData();
    this.isUserCanEdit = this.rolesService.canEditEndpoints(this.sharedService.getEnvironment());
    this.isUserInternal = this.rolesService.isUserInternal();
    this.isNewApiButtonDisplayed = this.sharedService.getEnvironment() === 'dev';
  }

  ngOnDestroy(): void {
  }

  private _lastGlobalEnvironmentHash: string;
  private _lastApiSandboxState: boolean;
  private _lastFormValidState: boolean;

  ngDoCheck(): void {
    const currentGlobalEnv = this.sharedService.getGlobalEnvironment();
    const currentGlobalEnvHash = JSON.stringify(currentGlobalEnv);
    
    if (this._lastGlobalEnvironmentHash !== currentGlobalEnvHash) {
      this.loadApiData();
      this.globalEnvironment = Object.assign({}, currentGlobalEnv);
      this._lastGlobalEnvironmentHash = currentGlobalEnvHash;
    }

    const currentSandboxState = this.api['isSandbox'];
    if (this._lastApiSandboxState !== currentSandboxState) {
      this._lastApiSandboxState = currentSandboxState;
      // Force form validation recalculation when sandbox state changes
      this._lastFormValidState = null;
    }

    const newFormValidState = currentSandboxState ? 
      this.checkFormIsValidSandbox() : 
      this.checkFormIsValid();
      
    if (this._lastFormValidState !== newFormValidState) {
      this.isFormDataValid = newFormValidState;
      this._lastFormValidState = newFormValidState;
    }
  }

  checkShowApiField(control) {
    if (control === 'requestType' || control === 'charsetType') {
      if (this.apiFormGroup && this.apiFormGroup.get('method')) {
        return (this.apiFormGroup.get('method').value !== 'GET'
          && this.apiFormGroup.get('method').value !== 'DELETE');
      }
      return false;
    }
    return true;
  }

  async loadApiData() {
    this.bannerCmsData = this.cmsService.cmsCall('4p66Klfl80gTGcgSaa3Enm', 'entry');

    this.apiList = await this.flowManagerService.getApis().pipe(
      map((el) => {
        forEach(el, (val, key) => (val.name = key));
        if (this.sharedService.getEnvironment() === 'prod') {
          forEach(this.sandboxApis, (val, key) => {
            if (!el.hasOwnProperty(key)) {
              val.name = key;
              el[`${key}_sandbox`] = val;
            }
          });
        }
        return el;
      })
    );

    if (this.sharedService.getEnvironment() === 'prod' && !this.sandboxApis && this.apiList) {
      await this.flowManagerService.getSandboxApisForProd().subscribe((el) => {
        forEach(el, (val, key) => {
          val.name = key;
          val.isSandbox = true;
        });
        this.sandboxApis = el;
        return el;
      });
      this.apiList.subscribe();
    }

    this.apiListForDisplay = await Object.assign(of(), this.apiList).pipe(
      map((item) => {
        const arr = values(item);
        if (this.sharedService.getEnvironment() === 'prod') {
          return sortBy(arr, [(o) => !o.isSandbox, (o) => o.name.toUpperCase()]);
        }
        return sortBy(arr, [(o) => o.name.toUpperCase()]);
      })
    );

    this.apiFormGroup = this.createApiForm();
  }

  onTabChange(event: MatTabChangeEvent) {
    this.activeTab = event.tab.textLabel;
    if(event.tab.textLabel === 'Test') {
      const methodValue = this.api.method;
      this.rawResultMode = this.getModeType(this.apiFormGroup.controls['responseType'].value);
      if (this.hasBody(methodValue) && this.bodyEditor.nativeElement?.attributes?.['data-keybinding-context'] === undefined) {
        this.monacoBodyEditor = this.setupEditor(this.bodyEditor.nativeElement, this.testBodyText, false, this.api.requestType);
        this.monacoBodyEditor.setValue("");
      }
    } else if(this.monacoBodyEditor) {
      this.monacoBodyEditor.dispose();
    }

  }

  onResetMonacoEditor() {
    if(this.bodyEditor?.nativeElement){
      this.disposeMonacoModels();
      this.bodyEditor.nativeElement.removeAttribute('data-keybinding-context');
    }
  }

  apiSelect(apiConfig: ApiConfig, apiName: string) {
    if (this.apiFormGroup.dirty) {
      Swal({
        title: 'Unsaved API will be lost',
        type: 'warning',
        confirmButtonText: 'Clear',
        cancelButtonText: 'Cancel',
      }).then((discard) => {
        if (discard) {
          this.openSelectedApi(apiConfig, apiName);
        }
      });
    } else {
      this.openSelectedApi(apiConfig, apiName);
    }
  }

  newApi() {
    if (this.apiFormGroup.dirty) {
      Swal({
        title: 'Unsaved API will be lost',
        type: 'warning',
        confirmButtonText: 'Clear',
        cancelButtonText: 'Cancel',
      }).then((discard) => {
        if (discard) {
          this.showNewApi();
        }
      });
    } else {
      this.showNewApi();
    }
  }

  showNewApi() {
    this.selectedApiName = '';
    this.isNewEndpoint = true;
    this.apiFormGroup.reset({
      poolName: DefaultPoolName
    });

    this.isTesting = false;
    this.engineVariables = {};
    this.showResults = false;
    this.testSelectedIndex = 0;
    this.isTimeOut = false;
    this.selectedApiUrlDisplay = '';
    this.testQueryParams = [{}];
    this.testHeaders = [{}];
    this.apiSelectedIndex = 0;
    this.saveClicked = false;
    this.disableRequestTypeValidation();
  }

   buildCMSConditions(type: string, field: string, channelId?: number, size?: string) {
      const cmsCondition: CMSConditions = {
        channelId: channelId,
        field: field,
        size: size,
        type: type
      };

      return cmsCondition;
  }

  private openSelectedApi(apiConfig: ApiConfig, apiName: string) {
    this.clearSelectedAPI();

    this.selectedApiName = apiName;
    this.api = apiConfig;
    this.apiFormGroup.get('apiName').setValue(apiName);

    // this.apiFormGroup.get('headers').setValue([{1:1}, {2:2}, {3:3}]);

    if (!apiConfig.headers) {
      apiConfig.headers = {'': ''};
    }

    for (const field in apiConfig) {
      if (field === 'headers') {
        for (const header in apiConfig[field]) {
          this.addHeader(header, apiConfig[field][header]);
        }
      } else {
        if (this.apiFormGroup.get(field)) {
          this.apiFormGroup.get(field).setValue(apiConfig[field]);
        }
      }
    }

    this.urlToQueryParams();
    this.selectedApiUrlDisplay = apiConfig.url;
    if (this.testQueryParams.length === 0) {
      this.testQueryParams = [{}];
    }
    this.rawResultMode = this.getModeType(this.apiFormGroup.controls['responseType'].value);
    this.disableRequestTypeValidation();
    this.clearTestingTabs();
  }

  addHeader(key= '', value= '') {
    const headers = this.apiFormGroup.get('headers') as UntypedFormArray;
    const headerForm = new UntypedFormGroup({
          'key': new UntypedFormControl(key, [ValidateNoWhitespaces]),
          'value': new UntypedFormControl(value, [ValidateNoWhitespaces])
        }
    );
    headers.push(headerForm);
  }

  removeHeader(index: number) {
    const headers = this.apiFormGroup.get('headers') as UntypedFormArray;
    headers.removeAt(index);
    this.apiFormGroup.markAsDirty();
  }

  private disableRequestTypeValidation() {
    const methodValue = this.apiFormGroup.get('method').value;

    if (methodValue === 'GET' || methodValue === 'DELETE') {
      this.apiFormGroup.get('requestType').disable();
    } else {
      this.apiFormGroup.get('requestType').enable();
    }
  }

  createApiForm(): UntypedFormGroup {
    this.api = new ApiConfig();
    if (this.apiFormGroup) {
      this.apiFormGroup.reset({
        apiName: '',
        poolName: [{value: this.api.poolName ? this.api.poolName : DefaultPoolName, disabled: true}],
        url: '',
        method: [this.api.method, ValidatorsRequired],
        responseType: [this.api.responseType, ValidatorsRequired],
        requestType: [this.api.requestType, ValidatorsRequired],
        charsetType: [this.api.charsetType ? this.api.charsetType : ''],
        elementValidation: [
          this.api.elementValidation,
          [ValidatorsRequired, ValidateNoWhitespaces, ValidatorsJsonPath],
        ],
        headers: new UntypedFormArray([]),
        baseElement: [this.api.baseElement, ValidatorsJsonPath],
        requestBody: [this.api.requestBody ? this.api.requestBody : ''],
        responseBody: [this.api.responseBody ? this.api.responseBody : ''],
      });
    }
    return this.formBuilder.group({
      apiName: [
        this.api.apiName,
        [ValidatorsRequired, ValidateNoWhitespaces, ValidatorsNameisNewEndpoint],
      ],
      poolName: [{value: this.api.poolName ? this.api.poolName : DefaultPoolName, disabled: true}],
      url: [
        this.api.url,
        [
          ValidatorsRequired,
          ValidatorsUrl(this.rolesService.isUserInternal()),
        ],
      ],
      method: [this.api.method, ValidatorsRequired],
      responseType: [this.api.responseType, ValidatorsRequired],
      requestType: [this.api.requestType, ValidatorsRequired],
      charsetType: [this.api.charsetType ? this.api.charsetType : ''],
      elementValidation: [
        this.api.elementValidation,
        [ValidatorsRequired, ValidateNoWhitespaces, ValidatorsJsonPath],
      ],
      headers: new UntypedFormArray([]),
      baseElement: [this.api.baseElement, ValidatorsJsonPath],
      requestBody: [this.api.requestBody ? this.api.requestBody : ''],
      responseBody: [this.api.responseBody ? this.api.responseBody : ''],
    });
  }

  deleteAPI() {
    if (this.isUserCanEdit) {
      if (this.isNewEndpoint) {
        this.isDeleting = true;
        setTimeout(() => {
          this.isNewEndpoint = false;
        }, 100);
        this.selectedApiName = '';
        this.apiFormGroup.reset();

        swalMini({
          type: 'success',
          title: 'New API Deleted Successfully ',
        });
        this.isDeleting = false;
      } else {
        this.isDeleting = true;
        let stageRequest: StageRequest;
        this.apiList.subscribe(
          (apiListLocal) => {
            delete apiListLocal[this.selectedApiName];
            stageRequest = {
              apiMap: apiListLocal,
              message: 'delete ' + this.selectedApiName,
            };
          },
          () => {},
          () => {
            this.flowManagerService
              .saveApis(this.selectedApiName, stageRequest)
              .subscribe((isSuccessful) => {
                if (isSuccessful) {
                  swalMini({
                    type: 'success',
                    title: this.selectedApiName + ' Deleted Successfully ',
                  });
                  this.selectedApiName = '';
                  this.isSaving = false;
                  this.loadApiData();
                } else {
                  this.isSaving = false;
                  swalMini({
                    type: 'error',
                    title: 'Something went wrong deleting ' + this.selectedApiName,
                  });
                }
                this.isDeleting = false;
              });
          }
        );
      }
    }
  }

  checkIfAPIIsSandboxOnSave() {
    if (this.api['isSandbox']) {
      this.showConfirmAPIIntegrationDialog()
    }
    else {
      this.saveAPI()
    }
  }

  saveAPI() {
    this.closeModal();
    this.isSaving = true;
    this.saveClicked = true;
    setTimeout(() => {}, 100);
    this.disableRequestTypeValidation();
    if (this.isUserCanEdit && this.apiFormGroup.valid) {
      let message = '';
      const newApiName = this.apiFormGroup.controls['apiName'].value;
      for (const controlName in this.apiFormGroup.controls) {
        const control = this.apiFormGroup.get(controlName);

        if (control.dirty && controlName === 'apiName') {
          message =
            message +
            controlName +
            ' changed from ' +
            this.selectedApiName +
            ' to ' +
            control.value +
            '\n';
        } else if (control.dirty) {
          message =
            message +
            controlName +
            ' changed from ' +
            this.api[controlName] +
            ' to ' +
            control.value +
            '\n';
        }
      }

      let stageRequest: StageRequest;
      this.apiList.subscribe((apiListLocal) => {
        // Check if API Name Exists
        if (this.apiFormGroup.controls['apiName'].dirty && apiListLocal[newApiName]) {
          const error: ValidationErrors = { message: ' Duplicate API Name found' };
          this.apiFormGroup.controls['apiName'].setErrors(error);
          this.isSaving = false;
          return;
        }

        // if API Name Changed
        if (!this.isNewEndpoint && this.apiFormGroup.controls['apiName'].dirty) {
          delete apiListLocal[this.selectedApiName];
        }

        const rawValue = this.apiFormGroup.getRawValue();
        rawValue.headers = this.toMap(rawValue.headers);
        apiListLocal[newApiName] = rawValue;

        apiListLocal[newApiName].url = apiListLocal[newApiName].url.trim().replace(/\s/g,"%20");

        const filteredApis = filter(apiListLocal, (o) => {
          if (!o.isSandbox && !o.name) {
            o.name = o.apiName;
          }
          return !o.isSandbox;
        });
        const newApiList = keyBy(filteredApis, 'name');

        stageRequest = {
          apiMap: newApiList,
          message: message || 'new api',
        };

        this.flowManagerService.saveApis(newApiName, stageRequest).subscribe((isSuccessful) => {
          if (isSuccessful) {
            swalMini({
              type: 'success',
              title: newApiName + ' Saved Successfully ',
            });
            this.loadApiData();
            this.selectedApiName = '';
            this.isNewEndpoint = false;
            this.isSaving = false;
          } else {
            this.isSaving = false;
            swalMini({
              type: 'error',
              title: 'Something went wrong saving ' + newApiName,
            });
          }
        });
      });
    } else {
      swalMini({
        title: 'Validation failed, please check for errors!',
        type: 'error',
      });
      this.isSaving = false;
    }
  }

  showConfirmAPIIntegrationDialog() {
    const dialogRef = this.dialog.open(this.saveAPIIntegration, {
      panelClass: 'saveAPIIntegration',
      width: '530px',
      height: '190px',
      disableClose: true,
    });
  }

  returnToAPIIntegration() {
    this.closeModal();
  }

  closeModal() {
    this.sharedService.setRequestRouteLoad('');
    this.sharedService.setRequestFlowLoad('');
    this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
    this.sharedService.requestEnvironment = Object.assign(
      {},
      this.sharedService.getGlobalEnvironment()
    );
    this.dialog.closeAll();
    //TODO JF: WHY WHY WHY I dont understand why the form needs to be reset after you click cancel!!
    if (this.activeFormService.getActiveForm() !== null) {
      this.activeFormService.removeTouchedForm(this.activeFormService.getActiveForm());
    }
  }

  cancel() {
    this.isCancelling = true;
    setTimeout(() => {
      this.clearSelectedAPI();
    }, 100);
  }

  clearSelectedAPI() {
    this.isCancelling =  false;
    this.apiFormGroup = this.apiFormGroup = this.createApiForm();
    this.isNewEndpoint = false;
    this.isTesting = false;
    this.engineVariables = {};
    this.showResults = false;
    this.testSelectedIndex = 0;
    this.apiSelectedIndex = 0;
    this.isTimeOut = false;
    this.saveClicked = false;
    this.selectedApiName = null;
    this.isDeleting = false;
  }

  showNewApiBtn() {
    return this.sharedService.getEnvironment() === 'dev';
  }

  testAPI() {
    const startDate = new Date();
    const endPointInterval = this.intervals(startDate)
        .pipe(
            // finalize(() => this.setupEditor(this.resultEditor.nativeElement, this.rawResultText, true, this.rawResultMode)),
            tap(() =>  {
              if (this.monacoResults) {
                this.monacoResults.dispose();
              }
            })
        )
        .subscribe(
      () => {
      },
      () => {},
      () => {
        this.isTimeOut = true;
      }
    );
    let sessionStore;
    const methodValue = this.api.method;
    this.showResults = true;
    this.isTimeOut = false;
    this.testSelectedIndex = 5;

    const body = {
      entityId: this.sharedService.getEntityId(),
      entityType: this.sharedService.getEntityType(),
      responseType: this.apiFormGroup.controls['responseType'].value,
      contentType: this.apiFormGroup.controls['requestType'].value,
      charsetType: this.apiFormGroup.controls['charsetType'].value,
      url: this.toPlainURL(),
      elementToValidate: this.apiFormGroup.controls['elementValidation'].value,
      method: this.apiFormGroup.controls['method'].value,
      baseElement: this.apiFormGroup.controls['baseElement'].value,
      headers: this.arrayToMap(this.testHeaders),
      preDefinedHeaders: this.toMap(this.apiFormGroup.getRawValue().headers),
      // can`t see the queryStrig at engine
      queryStrings: this.arrayToMap(this.testQueryParams),
    };

    this.engineVariables = {};
    this.isTesting = true;
    setTimeout(() => {
      this.rawResultText = '';
    }, 100);

    this.rawResultMode = this.getModeType(this.apiFormGroup.controls['responseType'].value);
    if (this.hasBody(methodValue) && this.monacoBodyEditor) {
      body['body'] = this.monacoBodyEditor.getValue();
    }

    const result$ = this.endpointTestingService.testEndpoint(this.api.requestType, body);

    const OnNext = (result) => {
      if (result['sessionStore']) {
        sessionStore = result['sessionStore'];
        if (this.rawResultMode === 'json') {
          if (sessionStore['http-error']) {
            this.rawResultText = JSON.stringify(sessionStore, null, 4);
          } else {
            let aceBody = '';
            const rawResponse = sessionStore['[[ACTION_NAME:raw-response]]'];
            if (rawResponse !== 'EMPTY_BODY' && rawResponse !== undefined) {
              try {
                aceBody = JSON.parse(rawResponse);
                this.rawResultText = JSON.stringify(aceBody, null, 4);
              } catch (exception) {
                this.rawResultText = rawResponse;
              }
            } else {
              this.rawResultText = rawResponse === 'EMPTY_BODY' ? 'EMPTY' : JSON.stringify(sessionStore, null, 4);
            }
          }
        } else {
          if (sessionStore['[[ACTION_NAME:raw-response]]']) {
            this.rawResultText = sessionStore['[[ACTION_NAME:raw-response]]'];
          } else {
            this.rawResultMode = 'json';
            this.rawResultText = JSON.stringify(sessionStore, null, 4);
          }
        }
      } else {
        this.rawResultMode = 'json';
        this.rawResultText = JSON.stringify(result, null, 4);
      }
      this.enginVariableDeserializer(sessionStore);
      this.isTesting = false;
      endPointInterval.unsubscribe();
      this.monacoResults = this.setupEditor(this.resultEditor.nativeElement, this.rawResultText, true, this.rawResultMode);
    };

    const onError = (error) => {
      this.rawResultMode = 'json';
      this.isTesting = false;
      this.rawResultText = JSON.stringify(error, null, 4);
      endPointInterval.unsubscribe();
      this.engineVariables = null;
    };

    result$.subscribe(OnNext, onError);
  }

  private setupEditor(elementRef, data, readOnly: boolean, language) {
    return monaco.editor.create(elementRef, {
      value: data,
      language: sanitizeContentType(language),
      minimap: {enabled: false},
      fontSize: 10,
      automaticLayout: true,
      theme: 'vs-light',
      readOnly: readOnly
    });
  }

  enginVariableDeserializer(sessionStore) {
    if (sessionStore && !sessionStore['http-error']) {
      this.engineVariables = {};
      const sessionVar = {};
      const sessionKeys = this.getObjectKeys(sessionStore);

      sessionKeys.forEach((key) => {
        if (typeof sessionStore[key] !== 'object' && key !== '[[ACTION_NAME:raw-response]]') {
          this.engineVariables[key] = sessionStore[key];
        }
      });
    }
  }

  getModeType(string) {
    if (string) {
      const modeType = string.substring(string.indexOf('/') + 1, string.length);
      return modeType;
    }
    return 'text';
  }

  hasBody(methodValue: string) {
    return methodValue !== 'GET' && methodValue !== 'DELETE';
  }

  showRequestBody(): boolean {
    const methodValue = this.apiFormGroup && this.apiFormGroup.get('method')
      ? this.apiFormGroup.get('method').value
      : null;
    return this.hasBody(methodValue);
  }

  intervals(startDate) {
    let currentDate;
    this.testDurationCounter = 0;

    return interval(1).pipe(
      map((x) => {
        currentDate = new Date().getTime();
        this.testDurationCounter = currentDate - startDate;
      }),
      timeInterval(),
      take(3500)
    );
  }

  private clearTestingTabs() {
    this.testHeaders = [{}];
    this.rawResultText = '';
    this.testBodyText = '';
    this.onResetMonacoEditor();
  }

  disposeMonacoModels() {
    monaco.editor.getModels().forEach(model => {
      model.dispose();
    });
  }

  addParamsKeyValue(params: [{}]) {
    params.push({ '': '' });
  }

  changeQueryParamsKey(oldKey: string, newKew: string) {
    const currentValue = this.testQueryParams[oldKey];
    delete this.testQueryParams[oldKey];
    this.testQueryParams[newKew] = currentValue;
  }

  changeQueryParamsValue(key: string, value: string) {
    this.testQueryParams[key] = value;
  }

  getObjectKeys(object: Object) {
    return Object.keys(object);
  }

  getObjectValue(object: Object) {
    return Object.values(object);
  }

  addLastParams(params: [{}]) {
    if (params) {
      const lastParam = params[params.length - 1];

      if (params[params.length - 1]) {
        const key = this.getObjectKeys(lastParam).toString().length !== 0;
        const value = this.getObjectValue(lastParam).toString().length !== 0;

        if (key && value) {
          params.push({ '': '' });
        }
      } else {
        params.push({ '': '' });
      }
    } else {
      params.push({ '': '' });
    }
  }

  onChange($event, isLast: boolean, index: number, params: [{}], listName: string) {
    const eventTarget: HTMLInputElement = $event.target;
    const key = (<HTMLInputElement>document.getElementById('key' + index + listName)).value;
    const value = (<HTMLInputElement>document.getElementById('value' + index + listName)).value;

    params[index] = { [key]: value };
    setTimeout(() => document.getElementById(eventTarget.id).focus(), 10);

    if (listName === 'QueryParams') {
      this.toQueryString();
    }
  }

  urlToQueryParams() {
    this.testQueryParams = [];
    if (this.apiFormGroup.get('url').valid) {
      let url = this.apiFormGroup.get('url').value;

      // this.endpointTestingService.urlToParams(url);

      if (url.indexOf('?') !== -1) {
        url = url.slice(this.api.url.indexOf('?') + 1, url.length);
        const queryParams = url.split('&');
        for (const queryParam of queryParams) {
          const param = queryParam.split('=');
          const key = param[0];
          const value = param[1];

          const newQP = {};
          newQP[key] = value;

          this.testQueryParams.push(newQP);
        }
      }
    }
  }

  private toQueryString() {
    let url;
    if (this.api.url.indexOf('?') !== -1) {
      url = this.api.url.slice(0, this.api.url.indexOf('?') + 1);
    } else {
      url = this.api.url;
    }
    this.selectedApiUrlDisplay = this.endpointTestingService.toURLString(url, this.testQueryParams);
  }

  private toPlainURL() {
    if (this.selectedApiUrlDisplay.indexOf('?') !== -1) {
      return this.selectedApiUrlDisplay.substring(0, this.selectedApiUrlDisplay.indexOf('?'));
    } else {
      return this.selectedApiUrlDisplay;
    }
  }

  private arrayToMap(array) {
    const newMap = {};
    array.map((params) => {
      const key = this.getObjectKeys(params).toString();
      const value = this.getObjectValue(params).toString();
      if (key.length !== 0) {
        newMap[key] = value;
      }
    });
    return newMap;
  }

  private toMap(array) {
    const newMap = {};
    array.map((params) => {
      if (params.key) {
        newMap[params.key] = params.value;
      }
      return;
    });
    return newMap;
  }

  onFocusOut(index: number, isLast: boolean, params: [{}], listName: string) {
    const key = (<HTMLInputElement>document.getElementById('key' + index + listName)).value;
    const value = (<HTMLInputElement>document.getElementById('value' + index + listName)).value;

    if (!key && !value && !isLast) {
      params.splice(index, 1);
      this.addLastParams(params);
    }
  }

  removeParam(index: number, params: [{}], listName: string) {
    params.splice(index, 1);
    if (listName === 'QueryParams') {
      this.toQueryString();
    }
  }

  checkFormIsValid() {
    return this.isUserCanEdit && this.apiFormGroup.valid && this.apiFormGroup.dirty;
  }

  checkFormIsValidSandbox() {
    return this.isUserCanEdit && this.apiFormGroup.valid;
  }

  getTestQueryParams() {
    return this.testQueryParams;
  }

  checkInvalidHeaders(i, prop, pairProp) {
    const headers = this.apiFormGroup.get('headers');
    if (headers) {
      const value = headers['controls'][i].controls[prop];
      const pairValue = headers['controls'][i].controls[pairProp];
      value.setErrors(undefined);
      if (pairValue.value && !value.value) {
        value.setErrors({ noPair: 'No pair value' });
      }
      return (!value.valid && (value.dirty || value.touched || this.saveClicked));
    }
  }
}

export function ValidateNoWhitespaces(control: AbstractControl) {
  const value: string = control.value;

  if (value) {
    if (!value.includes(' ')) {
      return null;
    }
    return { valid: true, message: ' Can`t Contain WhiteSpaces' };
  }
}

export function ValidatorsRequired(control: AbstractControl) {
  const value: string = control.value;

  if (value && value.length !== 0) {
    return null;
  }
  return { valid: true, message: ' Is Required' };
}

export function ValidatorsUrl(isUserInternal: boolean) {
  return (control: AbstractControl) => {
    const value: string = control.value;
    if (value) {
      if (
        isUserInternal
          ? value.match(URL_REGEX) || value.match(URL_REGEX_FOR_INTERNAL_USER)
          : value.match(URL_REGEX)
      ) {
        return null;
      }
      return { valid: true, message: 'Is Invalid' };
    }
  };
}

export function ValidatorsJsonPath(control: AbstractControl) {
  const value: string = control.value;
  if (value) {
    try {
      // jp.query(value,'$');
      return null;
    } catch (e) {
      return {
        valid: true,
        message:
          'Invalid JsonPath expression.' +
          'Please visit the following for ' +
          '<a href="http://goessner.net/articles/JsonPath/index.html#e2" target="_blank">' +
          'more info</a> and <a href="http://jsonpath.com" target="_blank">testing</a>',
      };
    }
  }
}

export function ValidatorsNameisNewEndpoint(control: AbstractControl) {
  const value: string = control.value;

  if (value) {
    if (value !== 'newEndpoint') {
      return null;
    }
    return { valid: true, message: ' needs to be changed' };
  }
}
